import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// ─── member care preferences vocabulary ─────────────────────────────────────
//
// How the member wants their first contact with the care team, captured at
// signup and shown to the care team where calls are booked. One row per member
// in `member_care_preferences`; every vocabulary below is the single source for
// that table's CHECKs, the member write, the staff read and the analytics
// forwarder's bounds (ADR-056 Rule 8).

/** Whether the member asked for a welcome call or would rather chat in the app. */
export const MEMBER_CONTACT_MODES = ['call', 'chat'] as const;
export const memberContactModeSchema = z.enum(MEMBER_CONTACT_MODES);
export type MemberContactMode = z.infer<typeof memberContactModeSchema>;

/**
 * The language the member needs on care calls. `en` means English with no
 * interpreter; every other value means the call needs an interpreter. This is
 * not `members.locale`, which is the language the app is shown in.
 */
export const CARE_CALL_LANGUAGES = ['en', 'es', 'ht', 'other'] as const;
export const careCallLanguageSchema = z.enum(CARE_CALL_LANGUAGES);
export type CareCallLanguage = z.infer<typeof careCallLanguageSchema>;

/**
 * Where the stored call language came from. `member`: the member picked it.
 * `app_language`: recorded from the language the member signed up in, for a
 * sponsor that offers Spanish. An `app_language` value never replaces a
 * `member` one.
 */
export const CARE_CALL_LANGUAGE_SOURCES = ['member', 'app_language'] as const;
export const careCallLanguageSourceSchema = z.enum(CARE_CALL_LANGUAGE_SOURCES);
export type CareCallLanguageSource = z.infer<typeof careCallLanguageSourceSchema>;

/** The fields a save can change, as named on the wire and in the change event. */
export const CARE_PREFERENCE_FIELDS = ['contactMode', 'callLanguage'] as const;
export const carePreferenceFieldSchema = z.enum(CARE_PREFERENCE_FIELDS);
export type CarePreferenceField = z.infer<typeof carePreferenceFieldSchema>;

/** Which fields a save changed: at least one, each named once. */
export const carePreferenceFieldListSchema = z
  .array(carePreferenceFieldSchema)
  .min(1)
  .max(CARE_PREFERENCE_FIELDS.length)
  .refine((fields) => new Set(fields).size === fields.length, {
    message: 'each changed field is named once',
  });

export const MEMBER_CARE_PREFERENCES_PATH = '/api/v1/members/me/care-preferences';

/**
 * The member's save. Each field is optional and an absent field is left as
 * stored; a body with neither field is refused. Strict, so a member id (or any
 * other key) in the body fails the parse: the member always comes from the
 * bearer token.
 */
export const putMemberCarePreferencesRequestSchema = z
  .object({
    contactMode: memberContactModeSchema.optional(),
    callLanguage: z
      .object({ language: careCallLanguageSchema, source: careCallLanguageSourceSchema })
      .strict()
      .optional(),
  })
  .strict()
  .refine((body) => body.contactMode !== undefined || body.callLanguage !== undefined, {
    message: 'at least one of contactMode or callLanguage is required',
  });
export type PutMemberCarePreferencesRequest = z.infer<typeof putMemberCarePreferencesRequestSchema>;

/** The member's stored preferences after a save. */
export const memberCarePreferencesSchema = z.object({
  contactMode: memberContactModeSchema.nullable(),
  callLanguage: careCallLanguageSchema.nullable(),
  callLanguageSource: careCallLanguageSourceSchema.nullable(),
  updatedAt: z.string(),
});
export type MemberCarePreferences = z.infer<typeof memberCarePreferencesSchema>;
export const memberCarePreferencesResponseSchema = apiEnvelope(memberCarePreferencesSchema);

/** What the staff member read carries, validated against the vocabularies. */
export const memberCarePreferencesSummarySchema = z.object({
  contactMode: memberContactModeSchema.nullable(),
  callLanguage: careCallLanguageSchema.nullable(),
  callLanguageSource: careCallLanguageSourceSchema.nullable(),
});
export type MemberCarePreferencesSummary = z.infer<typeof memberCarePreferencesSummarySchema>;

/**
 * The same summary as Atlas parses it: open strings, so a bundle loaded before
 * a vocabulary grows still parses the member read and shows its fallback text
 * instead of failing the page (ADR-135).
 */
export const memberCarePreferencesSummaryClientSchema = z.object({
  contactMode: z.string().nullable(),
  callLanguage: z.string().nullable(),
  callLanguageSource: z.string().nullable(),
});
export type MemberCarePreferencesSummaryClient = z.infer<
  typeof memberCarePreferencesSummaryClientSchema
>;

// ─── member.care_preferences.changed ────────────────────────────────────────
//
// Published after a save that changed a stored value. The member rides on the
// envelope's actor, never in `data`. Strict, so a stray field fails the
// producer's parse instead of reaching the bus.
export const memberCarePreferencesChangedPayloadSchema = z
  .object({
    contactMode: memberContactModeSchema.nullable(),
    callLanguage: careCallLanguageSchema.nullable(),
    callLanguageSource: careCallLanguageSourceSchema.nullable(),
    changedFields: carePreferenceFieldListSchema,
    // Injected by buildAnnotatedDomainEvent from the analytics catalogue.
    status: z.literal('updated').optional(),
  })
  .strict();
export type MemberCarePreferencesChangedPayload = z.infer<
  typeof memberCarePreferencesChangedPayloadSchema
>;
